"use client";
import React, { memo, useMemo } from "react";
import { AssetImage } from "@ospweb/sdk-ui";
import { cn } from "utils";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/CoPilot/360Reports/ReportItem/ReportLogo";

const I18n = I18nUtil.createScoped(Translations);

interface ReportLogoProps {
  /** Logo 宽度，默认 44 */
  logoWidth?: number;
  /** Logo 高度，默认 12 */
  logoHeight?: number;
  /** 文本样式类名，用于覆盖默认样式 */
  textClassName?: string;
  /** 样式变体，default 为原有样式，gradient 为渐变样式 */
  variant?: "default" | "gradient";
  className?: string;
}

export const ReportLogo: React.FC<ReportLogoProps> = memo(
  ({
    logoWidth = 56,
    logoHeight = 16,
    textClassName,
    variant = "default",
    className,
  }) => {
    // 文本拆分逻辑：从翻译文本中提取 "360" 和 "Report" 部分
    const { numberPart, reportPart, shouldSplit } = useMemo(() => {
      const text = I18n.t("360 Report");

      // 尝试匹配 "数字 + 空格 + 其他文本" 的模式
      const match = text.match(/^(\d+)\s+(.+)$/);

      if (match && variant === "gradient") {
        return {
          numberPart: match[1] + "°",
          reportPart: " " + match[2], // 添加空格以保持间距
          shouldSplit: true,
        };
      }

      return {
        numberPart: "",
        reportPart: text,
        shouldSplit: false,
      };
    }, [variant]);

    const renderText = () => {
      if (variant === "gradient" && shouldSplit) {
        return (
          <>
            <span
              className={cn(
                "body_medium_s text-content_brand_primary whitespace-nowrap",
                textClassName,
              )}
            >
              {numberPart}
            </span>
            <span
              className={cn(
                "body_medium_s bg-gradient-to-r from-background_brand_overlay100 to-white bg-clip-text text-transparent whitespace-nowrap",
                textClassName,
              )}
              style={{
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {reportPart}
            </span>
          </>
        );
      }

      // 默认样式：保持原有显示方式
      return (
        <span
          className={cn(
            "body_medium_s text-content_primary whitespace-nowrap",
            textClassName,
          )}
        >
          {I18n.t("360 Report")}
        </span>
      );
    };

    return (
      <div className={cn("flex items-center gap-1", className)}>
        <picture>
          <source
            srcSet="/edgen_logo_dark.svg"
            type="image/svg+xml"
            media="(min-width: 960px)"
          />
          <AssetImage
            src="/edgen_logo_light.svg"
            alt=""
            width={logoWidth}
            height={logoHeight}
          />
        </picture>
        <i className="block w-1 h-1 rounded-full bg-background_white_overlay20 shrink-0" />
        <div className="flex items-center">{renderText()}</div>
      </div>
    );
  },
);

ReportLogo.displayName = "ReportLogo";
